import { Component, input, output, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FHIRBundleEntry,
  ReferencedResources,
  FHIROrganization,
  FHIRPractitioner,
  FHIREncounter,
  FHIRCondition,
  FHIRAllergyIntolerance,
  FHIRMedicationAdministration,
  FHIRMedicationRequest,
  FHIRMedicationStatement,
  FHIRProcedure,
  FHIRObservation,
} from '../../../models';
import { FhirMapperService } from '../../../../core/services';
import { EncounterSectionComponent } from './sections/encounter-section.component';
import { ConditionsSectionComponent } from './sections/conditions-section.component';
import { AllergiesSectionComponent } from './sections/allergies-section.component';
import { MedicationsSectionComponent } from './sections/medications-section.component';
import { ProceduresSectionComponent } from './sections/procedures-section.component';
import { ObservationsSectionComponent } from './sections/observations-section.component';
import { RiskSectionComponent } from './sections/risk-section.component';
import { PractitionersSectionComponent } from './sections/practitioners-section.component';
import { OrganizationsSectionComponent } from './sections/organizations-section.component';
import { FamilyHistorySectionComponent } from './sections/family-history-section.component';
import { DocumentReferencesSectionComponent } from './sections/document-references-section.component';

@Component({
  selector: 'app-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    EncounterSectionComponent,
    ConditionsSectionComponent,
    AllergiesSectionComponent,
    MedicationsSectionComponent,
    ProceduresSectionComponent,
    ObservationsSectionComponent,
    RiskSectionComponent,
    PractitionersSectionComponent,
    OrganizationsSectionComponent,
    FamilyHistorySectionComponent,
    DocumentReferencesSectionComponent,
  ],
  templateUrl: './detail-modal.component.html',
  styleUrl: './detail-modal.component.scss',
})
export class DetailModalComponent {
  private readonly mapper = inject(FhirMapperService);

  readonly entry = input.required<FHIRBundleEntry>();
  readonly referencedResources = input.required<ReferencedResources>();
  readonly closeClick = output<void>();

  // ── Datos derivados del entry ────────────────────────────────────────────

  readonly composition = computed(() => this.entry().resource);

  readonly encounter = computed<FHIREncounter | null>(() => {
    const encRef: string = this.composition()?.encounter?.reference || '';
    if (!encRef.includes('/')) return null;
    const [, id] = encRef.split('/');
    return this.referencedResources().encounters?.find((e) => e.id === id) ?? null;
  });

  readonly encounterData = computed(() => this.mapper.mapEncounter(this.encounter() ?? undefined));

  readonly patient = computed(() => this.referencedResources().patients?.[0] ?? null);

  readonly patientData = computed(() => this.mapper.mapPatient(this.patient() ?? undefined));

  readonly organizations = computed<FHIROrganization[]>(() => {
    const r = this.composition();
    const refs = this.referencedResources();
    if (!r || !refs) return [];

    const orgIds = new Set<string>();
    const addRef = (ref: string) => {
      if (ref?.includes('/')) {
        const [type, id] = ref.split('/');
        if (type === 'Organization' && id) orgIds.add(id);
      }
    };

    addRef(r.custodian?.reference);
    (r.attester || []).forEach((a: any) => addRef(a.party?.reference));
    (r.author || []).forEach((a: any) => addRef(a.reference));

    return (refs.organizations || []).filter((o) => o.id && orgIds.has(o.id));
  });

  readonly practitioners = computed<FHIRPractitioner[]>(() => {
    const r = this.composition();
    const refs = this.referencedResources();
    if (!r || !refs) return [];

    const practIds = new Set<string>();
    const addRef = (ref: string) => {
      if (ref?.includes('/')) {
        const [type, id] = ref.split('/');
        if (type === 'Practitioner' && id) practIds.add(id);
      }
    };

    (r.author || []).forEach((a: any) => addRef(a.reference));
    (r.attester || []).forEach((a: any) => addRef(a.party?.reference));

    // Practitioners del encounter
    const enc = this.encounter();
    if (enc) {
      (enc.participant || []).forEach((p) => {
        const ref = p.individual?.reference || '';
        if (ref.includes('/')) {
          const [type, id] = ref.split('/');
          if (type === 'Practitioner' && id) practIds.add(id);
          if (type === 'PractitionerRole' && id) {
            const pr = refs.practitionerRoles?.find((x: any) => x.id === id);
            if (pr?.practitioner?.reference?.includes('/')) {
              const [, practId] = pr.practitioner.reference.split('/');
              if (practId) practIds.add(practId);
            }
          }
        }
      });
    }

    return (refs.practitioners || []).filter((p) => p.id && practIds.has(p.id));
  });

  // ── Recursos de las secciones ────────────────────────────────────────────

  private sectionIds = computed<Set<string>>(() => {
    const ids = new Set<string>();
    const sections = this.composition()?.section || [];
    const traverse = (secs: any[]) => {
      secs.forEach((sec) => {
        (sec.entry || []).forEach((e: any) => {
          const ref: string = e.reference || '';
          if (ref.includes('/')) {
            const [, id] = ref.split('/');
            if (id) ids.add(id);
          }
        });
        if (sec.section) traverse(sec.section);
      });
    };
    traverse(sections);
    return ids;
  });

  private encRef = computed<string>(() => this.composition()?.encounter?.reference || '');

  readonly conditions = computed<FHIRCondition[]>(() => {
    const ids = this.sectionIds();
    return (this.referencedResources().conditions || []).filter((c) => ids.has(c.id!));
  });

  readonly allergies = computed<FHIRAllergyIntolerance[]>(() => {
    const ids = this.sectionIds();
    return (this.referencedResources().allergyIntolerances || []).filter((a) => ids.has(a.id!));
  });

  readonly medAdministrations = computed<FHIRMedicationAdministration[]>(() => {
    const ids = this.sectionIds();
    return (this.referencedResources().medicationAdministrations || []).filter((m) =>
      ids.has(m.id!),
    );
  });

  readonly medRequests = computed<FHIRMedicationRequest[]>(() => {
    const ids = this.sectionIds();
    return (this.referencedResources().medicationRequests || []).filter((m) => ids.has(m.id!));
  });

  readonly medStatements = computed<FHIRMedicationStatement[]>(() => {
    const ids = this.sectionIds();
    return (this.referencedResources().medicationStatements || []).filter((m) => ids.has(m.id!));
  });

  readonly procedures = computed<FHIRProcedure[]>(() => {
    const ids = this.sectionIds();
    return (this.referencedResources().procedures || []).filter((p) => ids.has(p.id!));
  });

  readonly observations = computed<FHIRObservation[]>(() => {
    const encRef = this.encRef();
    const ids = this.sectionIds();
    return (this.referencedResources().observations || []).filter((o) => {
      const oEncRef: string = (o as any).encounter?.reference || '';
      if (oEncRef) {
        const norm = (r: string) => r.replace(/^#/, '').replace(/^.*\//, '');
        return norm(oEncRef) === norm(encRef);
      }
      return ids.has(o.id!);
    });
  });

  readonly riskAssessments = computed<any[]>(() => {
    const encRef = this.encRef();
    const ids = this.sectionIds();
    return (this.referencedResources().riskAssessments || []).filter((r: any) => {
      const rEncRef: string = r.encounter?.reference || '';
      if (rEncRef) {
        const norm = (s: string) => s.replace(/^#/, '').replace(/^.*\//, '');
        return norm(rEncRef) === norm(encRef);
      }
      return ids.has(r.id);
    });
  });

  readonly serviceRequests = computed<any[]>(() => {
    const encRef = this.encRef();
    const ids = this.sectionIds();
    return (this.referencedResources().serviceRequests || []).filter((s: any) => {
      const sEncRef: string = s.encounter?.reference || '';
      if (sEncRef) {
        const norm = (r: string) => r.replace(/^#/, '').replace(/^.*\//, '');
        return norm(sEncRef) === norm(encRef);
      }
      return ids.has(s.id);
    });
  });

  readonly documentReferences = computed<any[]>(() => {
    const encRef = this.encRef();
    const ids = this.sectionIds();
    return (this.referencedResources().documentReferences || []).filter((d: any) => {
      const encounters: any[] = d.context?.encounter || [];
      if (encounters.length > 0) {
        const norm = (r: string) => r.replace(/^#/, '').replace(/^.*\//, '');
        return encounters.some((e: any) => norm(e.reference || '') === norm(encRef));
      }
      return ids.has(d.id);
    });
  });

  readonly familyHistories = computed<any[]>(() => {
    const ids = this.sectionIds();
    return (this.referencedResources().familyMemberHistories || []).filter((f: any) =>
      ids.has(f.id),
    );
  });

  // ── Helpers de UI ────────────────────────────────────────────────────────

  formatDate(date: string): string {
    if (!date) return '—';
    return /^\d{4}-\d{2}-\d{2}/.test(date) ? date.slice(0, 10) : date;
  }

  getStatusBadge(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'finished' || s === 'completed' || s === 'final') return 'bg-success';
    if (s === 'in-progress' || s === 'active') return 'bg-primary';
    if (s === 'planned' || s === 'preliminary') return 'bg-warning text-dark';
    if (s === 'cancelled' || s === 'not-done') return 'bg-danger';
    return 'bg-secondary';
  }

  getClaseBadge(codigo: string): { color: string; label: string } {
    const c = (codigo || '').toUpperCase();
    if (c === 'IMP') return { color: 'bg-danger', label: 'Hospitalización' };
    if (c === 'AMB') return { color: 'bg-success', label: 'Ambulatorio' };
    if (c === 'EMER') return { color: 'bg-warning text-dark', label: 'Urgencias' };
    if (c === 'VR') return { color: 'bg-info', label: 'Virtual' };
    return { color: 'bg-secondary', label: codigo || 'N/A' };
  }

  getConditionBadge(estado: string): string {
    const s = (estado || '').toLowerCase();
    if (s.includes('active') || s.includes('activ')) return 'bg-danger';
    if (s.includes('resolved') || s.includes('resuel')) return 'bg-success';
    if (s.includes('inactive') || s.includes('inactiv')) return 'bg-secondary';
    return 'bg-info';
  }

  getAllergyBadge(estado: string): string {
    const s = (estado || '').toLowerCase();
    if (s.includes('active') || s.includes('activ')) return 'bg-danger';
    if (s.includes('resolved') || s.includes('resuel')) return 'bg-success';
    return 'bg-secondary';
  }

  getMedStatusBadge(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'completed') return 'bg-success';
    if (s === 'active') return 'bg-primary';
    if (s === 'stopped' || s === 'cancelled') return 'bg-danger';
    if (s === 'on-hold') return 'bg-warning text-dark';
    return 'bg-secondary';
  }

  getProcedureBadge(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'completed') return 'bg-success';
    if (s === 'in-progress') return 'bg-primary';
    if (s === 'not-done') return 'bg-danger';
    return 'bg-secondary';
  }

  getOrgNIT(org: FHIROrganization): string {
    return (
      (org.identifier || []).find((id) =>
        (id.type?.coding || []).some(
          (c) => c.code?.toUpperCase() === 'NIT' || c.code?.toUpperCase() === 'TAX',
        ),
      )?.value || ''
    );
  }

  getOrgCodigo(org: FHIROrganization): string {
    return (
      (org.identifier || []).find((id) =>
        (id.type?.coding || []).some(
          (c) => c.code?.toUpperCase() === 'CODIGOPRESTADOR' || c.code?.toUpperCase() === 'PRN',
        ),
      )?.value || ''
    );
  }

  getCodingDisplay(coding: any): string {
    return coding?.display || coding?.code || '';
  }

  getFirstCoding(concept: any): any {
    return concept?.coding?.[0] ?? null;
  }
}
